import React from "react";
import Skeleton from "../ui/Skeleton";

/** Linhas do histórico de consumo (aba Histórico e "Atividade recente"). */
export const SettingsHistoryRowsSkeleton: React.FC<{ rows?: number }> = ({
  rows = 6,
}) => (
  <ul className="divide-y divide-gray-50">
    {Array.from({ length: rows }, (_, i) => (
      <li key={i} className="flex items-center gap-3 px-4 py-3">
        <Skeleton
          variant="rectangular"
          width={36}
          height={36}
          className="rounded-xl shrink-0"
        />
        <div className="flex-1 min-w-0 space-y-1.5">
          <Skeleton variant="text" width="35%" height={14} />
          <Skeleton variant="text" width="55%" height={11} />
        </div>
        <Skeleton variant="text" width={48} height={16} />
      </li>
    ))}
  </ul>
);

/** Aba Planos e recargas: seletores + 3 cards de oferta. */
export const SettingsPlansGridSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="rounded-2xl border border-purple-200 bg-white p-5 space-y-3"
      >
        <Skeleton variant="text" width={90} height={16} />
        <Skeleton variant="text" width="70%" height={11} />
        <Skeleton variant="text" width={140} height={28} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={48}
          className="rounded-xl"
        />
        <div className="space-y-2 pt-1">
          {[1, 2, 3, 4].map((j) => (
            <Skeleton key={j} variant="text" width="65%" height={12} />
          ))}
        </div>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={36}
          className="rounded-lg"
        />
      </div>
    ))}
  </div>
);

/** Segmento Envios: select de quantidade, total e botão. */
export const SettingsPackagesSkeleton: React.FC = () => (
  <div className="space-y-4">
    <Skeleton
      variant="rectangular"
      width="100%"
      height={40}
      className="rounded-lg"
    />
    <div className="flex items-end justify-between">
      <Skeleton variant="text" width={140} height={30} />
      <Skeleton variant="text" width={110} height={14} />
    </div>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={36}
      className="rounded-lg"
    />
  </div>
);

export const SettingsPlansSkeleton: React.FC = () => (
  <div className="space-y-4">
    {/* Cartões de opção (Assinatura, Recarga, Envios) + linha do ciclo */}
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      {[1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          width="100%"
          height={62}
          className="rounded-2xl"
        />
      ))}
    </div>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={52}
      className="rounded-xl"
    />
    <SettingsPlansGridSkeleton />
  </div>
);

/** Aba Histórico: filtros (tipo + período), 10 linhas e paginação. */
export const SettingsHistorySkeleton: React.FC = () => (
  <div className="space-y-3">
    {/* Consumo/Compras, tipo de uso, período e exportar CSV */}
    <div className="flex flex-wrap items-center gap-2">
      <Skeleton
        variant="rectangular"
        width={150}
        height={32}
        className="rounded-lg"
      />
      <Skeleton
        variant="rectangular"
        width={160}
        height={32}
        className="rounded-lg"
      />
      <Skeleton
        variant="rectangular"
        width={250}
        height={32}
        className="rounded-lg"
      />
      <Skeleton
        variant="rectangular"
        width={120}
        height={32}
        className="rounded-lg sm:ml-auto"
      />
    </div>
    <div className="border border-purple-200 rounded-2xl overflow-hidden bg-white">
      <SettingsHistoryRowsSkeleton rows={10} />
    </div>
    <div className="flex items-center justify-center gap-2">
      <Skeleton
        variant="rectangular"
        width={92}
        height={32}
        className="rounded-lg"
      />
      <Skeleton variant="text" width={36} height={12} />
      <Skeleton
        variant="rectangular"
        width={92}
        height={32}
        className="rounded-lg"
      />
    </div>
  </div>
);

/** Aba Custos por ação: grade de pares rótulo/valor. */
export const SettingsCostsSkeleton: React.FC = () => (
  <div className="space-y-3">
    <Skeleton
      variant="rectangular"
      width={384}
      height={36}
      className="rounded-lg max-w-full"
    />
    <Skeleton
      variant="rectangular"
      width="100%"
      height={52}
      className="rounded-2xl"
    />
    {/* Grupos por área: título + linhas com ícone, nome, saldo e custo */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {[4, 3, 3, 4].map((rows, g) => (
        <div
          key={g}
          className="rounded-2xl border border-purple-200 bg-white px-4 py-3 space-y-3"
        >
          <Skeleton variant="text" width={140} height={14} />
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton
                variant="rectangular"
                width={32}
                height={32}
                className="rounded-lg shrink-0"
              />
              <div className="flex-1 space-y-1.5">
                <Skeleton variant="text" width="50%" height={13} />
                <Skeleton variant="text" width="35%" height={10} />
              </div>
              <Skeleton variant="text" width={64} height={14} />
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/** Card "Consumo diário" + "Para onde vão os créditos" da Visão geral. */
export const SettingsUsageSummarySkeleton: React.FC = () => (
  <div className="rounded-2xl border border-purple-200 bg-white p-4">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        <Skeleton variant="text" width={120} height={14} />
        <div className="flex h-32 items-end gap-[2px]">
          {Array.from({ length: 30 }, (_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width="100%"
              height={`${20 + ((i * 37) % 70)}%`}
              className="flex-1 rounded-t-[4px] opacity-60"
            />
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <Skeleton variant="text" width={180} height={14} />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton variant="text" width="60%" height={12} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={6}
              className="rounded-full"
            />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** Aba Visão geral: KPIs, saldos de envio e atividade recente. */
export const SettingsOverviewSkeleton: React.FC = () => (
  <div className="space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-2xl border border-purple-200 bg-white px-4 py-3 space-y-2"
        >
          <Skeleton variant="text" width={110} height={12} />
          <Skeleton variant="text" width={90} height={26} />
          <Skeleton variant="text" width={80} height={10} />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-2xl border border-purple-200 bg-white px-4 py-3"
        >
          <Skeleton
            variant="rectangular"
            width={36}
            height={36}
            className="rounded-xl shrink-0"
          />
          <div className="flex-1 space-y-1.5">
            <Skeleton variant="text" width="40%" height={14} />
            <Skeleton variant="text" width="60%" height={11} />
          </div>
          <Skeleton variant="text" width={80} height={20} />
        </div>
      ))}
    </div>
    <div className="border border-purple-200 rounded-2xl overflow-hidden bg-white">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-50">
        <Skeleton variant="text" width={120} height={14} />
        <Skeleton
          variant="rectangular"
          width={110}
          height={32}
          className="rounded-lg"
        />
      </div>
      <SettingsHistoryRowsSkeleton rows={5} />
    </div>
  </div>
);

/** Página inteira: cabeçalho compacto com chips e abas + conteúdo da aba aberta. */
export const SettingsSkeleton: React.FC<{
  tab?: "overview" | "history" | "costs" | "plans";
}> = ({ tab = "overview" }) => {
  return (
    <div className="relative z-10 space-y-4 animate-in fade-in duration-500">
      <section className="bg-white border border-purple-200 rounded-3xl overflow-hidden">
        <div className="h-1.5 bg-purple-100" />
        <div className="px-4 sm:px-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Skeleton
                variant="rectangular"
                width={48}
                height={48}
                className="rounded-xl shrink-0"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <Skeleton variant="text" width={180} height={18} />
                <div className="flex flex-wrap items-center gap-1.5">
                  {[110, 110, 110].map((w, i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      width={w}
                      height={26}
                      className="rounded-lg"
                    />
                  ))}
                </div>
              </div>
            </div>
            <Skeleton
              variant="rectangular"
              width={190}
              height={40}
              className="rounded-xl max-sm:w-full!"
            />
          </div>
          {/* Abas de navegação (a de compra também tem botão no cabeçalho) */}
          <div className="mt-3 -mx-4 sm:-mx-5 border-t border-purple-100 px-5 py-2 flex items-center gap-6">
            {[100, 110, 140, 150].map((w, i) => (
              <Skeleton key={i} variant="text" width={w} height={16} />
            ))}
          </div>
        </div>
      </section>
      {tab === "plans" ? (
        <SettingsPlansSkeleton />
      ) : tab === "costs" ? (
        <SettingsCostsSkeleton />
      ) : tab === "history" ? (
        <SettingsHistorySkeleton />
      ) : (
        <SettingsOverviewSkeleton />
      )}
    </div>
  );
};

export default SettingsSkeleton;
